import { useCallback, useEffect, useRef, useState } from "react";

import { amazonSpOAuthLoginContextStorageKey, createAmazonSpOAuthLoginContext } from "./amazonSpOAuth";
import {
  prepareStoreOAuthAuthorization,
  type ConnectionOAuthProvider,
  type PreparedStoreOAuth,
  type StoreOAuthProvider,
} from "./storeBindingOAuthClient";
import { oauthErrorMessage, openOAuthPopup, parseOAuthPopupMessage } from "./storeBindingOAuthPopup";

export type ConnectionOAuthWork<Provider extends ConnectionOAuthProvider> =
  | { status: "idle" }
  | {
      status: "running" | "failed" | "succeeded";
      provider: Provider;
      detail: string;
    };
export type StoreOAuthWork = ConnectionOAuthWork<StoreOAuthProvider>;

interface ConnectionOAuthOptions<Provider extends ConnectionOAuthProvider> {
  organizationId: string;
  onPrepared?: (prepared: PreparedStoreOAuth, provider: Provider) => Promise<void>;
  prepare: (input: {
    organizationId: string;
    provider: Provider;
    shopDomain?: string;
  }) => Promise<PreparedStoreOAuth>;
  verify: (operationId: string, provider: Provider) => Promise<string>;
  onSucceeded?: () => void;
}

export function useConnectionOAuth<Provider extends ConnectionOAuthProvider>(
  options: ConnectionOAuthOptions<Provider>,
) {
  const optionsRef = useRef(options);
  useEffect(() => { optionsRef.current = options; });
  const [work, setWork] = useState<ConnectionOAuthWork<Provider>>({ status: "idle" });
  const current = useRef<{ popup: Window; provider: Provider; operationId: string; timer: number; checking: boolean } | null>(null);
  const stop = useCallback(() => {
    const flow = current.current;
    if (flow) window.clearInterval(flow.timer);
    current.current = null;
  }, []);
  useEffect(() => {
    const receive = async (event: MessageEvent<unknown>) => {
      const flow = current.current;
      if (!flow || event.source !== flow.popup || flow.checking) return;
      const message = parseOAuthPopupMessage(event);
      if (!message || message.provider !== flow.provider || (message.operationID && message.operationID !== flow.operationId)) return;
      if (message.status === "succeeded" && (!flow.operationId || message.operationID !== flow.operationId)) return;
      flow.checking = true;
      window.clearInterval(flow.timer);
      try {
        if (message.status !== "succeeded") throw new Error(message.error ?? "oauth_failed");
        setWork({ status: "running", provider: flow.provider, detail: "正在确认服务端授权结果" });
        const detail = await optionsRef.current.verify(flow.operationId, flow.provider);
        if (current.current !== flow) return;
        stop();
        setWork({ status: "succeeded", provider: flow.provider, detail });
        optionsRef.current.onSucceeded?.();
      } catch (error) {
        if (current.current !== flow) return;
        stop();
        setWork({ status: "failed", provider: flow.provider, detail: oauthErrorMessage(error) });
      }
    };
    window.addEventListener("message", receive);
    return () => { window.removeEventListener("message", receive); stop(); };
  }, [stop]);
  const start = useCallback(async (provider: Provider, label: string, shopDomain?: string) => {
    if (current.current) return;
    // 保留用户手势：任何 await 之前同步打开空白官方授权小窗。
    const popup = openOAuthPopup(provider, label);
    if (!popup) { setWork({ status: "failed", provider, detail: "浏览器拦截了授权窗口，请允许本站打开弹窗" }); return; }
    const startedAt = Date.now();
    const flow = { popup, provider, operationId: "", timer: 0, checking: false };
    current.current = flow;
    flow.timer = window.setInterval(() => {
      const timedOut = Date.now() - startedAt >= 300_000;
      if (!popup.closed && !timedOut) return;
      stop();
      if (timedOut && !popup.closed) popup.close();
      setWork({ status: "failed", provider, detail: timedOut ? "授权等待超时，请重新发起授权" : "授权窗口已关闭，可重新发起" });
    }, 250);
    setWork({ status: "running", provider, detail: "正在准备安全授权窗口" });
    try {
      const prepared = await optionsRef.current.prepare({ organizationId: optionsRef.current.organizationId, provider, shopDomain });
      if (current.current !== flow || popup.closed) return;
      flow.operationId = prepared.operationId;
      // 服务端先绑定 operation 归属；之后才允许离开空白页，刷新可恢复。
      await optionsRef.current.onPrepared?.(prepared, provider);
      if (current.current !== flow || popup.closed) return;
      if (provider === "amazon_sp") popup.sessionStorage.setItem(amazonSpOAuthLoginContextStorageKey, JSON.stringify(createAmazonSpOAuthLoginContext(prepared.authorizationUrl, window.location.origin)));
      popup.location.assign(prepared.authorizationUrl);
      popup.focus();
      setWork({ status: "running", provider, detail: `请在小窗中完成 ${label} 授权` });
    } catch (error) {
      if (current.current !== flow) return;
      stop();
      if (!popup.closed) popup.close();
      setWork({ status: "failed", provider, detail: oauthErrorMessage(error) });
    }
  }, [stop]);
  return { work, start, reset: () => { if (!current.current) setWork({ status: "idle" }); } };
}

interface StoreOAuthOptions {
  organizationId: string;
  onPrepared?: (
    prepared: PreparedStoreOAuth,
    provider: StoreOAuthProvider,
  ) => Promise<void>;
  verify: (operationId: string, provider: StoreOAuthProvider) => Promise<string>;
  onSucceeded?: () => void;
}

export function useStoreBindingOAuth(options: StoreOAuthOptions) {
  return useConnectionOAuth({
    ...options,
    prepare: prepareStoreOAuthAuthorization,
  });
}
